import type { IntlFormatters } from 'react-intl';
import {
    ORGANIZATIONS_BY_SPORT,
    WORLD_REGIONS_CODES,
} from '@gin-betting/constants';
import core, { API, App, GQL, View } from '@gin-betting/core';
import { Filters } from '@gin-betting/filters';
import { alpha3ToAlpha2, getName } from 'i18n-iso-countries';
import { Map } from 'immutable';
import {
    concat,
    differenceBy,
    findIndex,
    flatMap,
    forEach,
    get,
    groupBy,
    includes,
    intersection,
    map,
    omit,
    partition,
    pickBy,
    sortBy,
    sumBy,
    without,
} from 'lodash';

import {
    CategorizerTournamentMetaKey,
    LIVE_SPORTEVENT_STATUSES,
    OUTRIGHTS_ONLY_SPORTS,
    Product,
} from 'app-constants';
import globalMessages from 'messages';
import features from 'services/feature-manager';
import localeManager from 'services/locale-manager';
import UserDevice from 'services/user-device';
import {
    checkIsDynamicCategorizer,
    checkKeyInObj,
    getByKey,
    getProductName,
    getRouteBySportType,
    getVariablesForLiveSportEvent,
    isFavoritePage,
} from 'utils';
import { CategorizerWidgetOptions } from '../../types';
import {
    ACTIVE_COUNTRY_KEY_DIVIDER,
    CATEGORIZER_STATIC_SPORTS_BY_TYPE,
    customFlagsMapping,
    EXCLUDED_WORLD_REGIONS,
    ORDER_CATEGORIZER_ITEMS_MAPPING,
    UNGROUPED_CATEGORIES_KEY,
    WORLD_REGIONS,
} from './constants';
import {
    CategorizerItemByOrganizations,
    LocationInfo,
    TournamentInfo,
} from './types';

const {
    constants: { SportType },
    services: { stringify, getHistory },
    utils: {
        getAppliedFilters,
        checkOnRoute,
        isOneOfPages,
        isInnerPage,
        getSportsBySportType,
        getBettingOptions,
        getSportTypeBySportTags,
        getSportTypeFromLocation,
        getDefaultVariables,
    },
} = core;

export function filterMatches(
    filters: Partial<App.Filters>,
    pathname?: string
): void {
    const searchObj = { ...getAppliedFilters(), ...filters };

    const history = getHistory();
    const validSearchObj: typeof searchObj = pickBy(searchObj, v => !!v);

    const search = stringify(validSearchObj);

    history.replace({
        ...history.location,
        hash: window.location.hash,
        search,
        ...(pathname && { pathname }),
    });
}

export function canFilterByTournamentOnMobile(): boolean {
    return isOneOfPages(
        [
            App.Routes.UPCOMING_LIST,
            App.Routes.RESULTS_LIST,
            App.Routes.ESPORT,
            App.Routes.CLASSIC,
        ],
        { exact: true }
    );
}

export function canFilterByTimePeriods(): boolean {
    return checkOnRoute(App.Routes.RESULTS_LIST);
}

export function canFilterByLivePeriods(): boolean {
    return checkOnRoute(App.Routes.LIVE_LIST);
}

export const getFormattedCountryCode = (countryCode: string): string | null =>
    getByKey(customFlagsMapping, countryCode) ||
    alpha3ToAlpha2(countryCode) ||
    null;

export function getTournamentCountryCode(
    { meta }: API.CategorizerItem,
    isAlpha3Format: boolean = false
): string | null {
    const metaCountryCode = meta.find(
        ({ name }) => name === CategorizerTournamentMetaKey.CountryCode
    )?.value;

    if (!metaCountryCode) return null;

    if (metaCountryCode === 'EU') {
        return 'WW-EUR';
    }

    if (isAlpha3Format) {
        return getByKey(customFlagsMapping, metaCountryCode) || metaCountryCode;
    }

    if (metaCountryCode.length !== 3) {
        return metaCountryCode;
    }

    return getFormattedCountryCode(metaCountryCode);
}

export function getSearchString(changedFilters: Partial<App.Filters>): string {
    const currentFilters = getAppliedFilters();
    const nextFilters = {
        ...currentFilters,
        ...changedFilters,
    };

    return stringify(nextFilters);
}

export function getSportsWithCheckedTournaments(
    sports: API.CategorizerItem[],
    tournamentIds: string[]
): string[] {
    if (!tournamentIds.length) return [];

    return sports
        .filter(({ children }) =>
            children.some(({ value }) => tournamentIds.includes(value))
        )
        .map(item => item.value);
}

export function getSortedSportsBySportType({
    sportType,
    sports,
    categorizerItems,
    sportsOrder,
}: {
    sportType: App.SportType | null;
    sports: Map<string, View.Sport>;
    categorizerItems: API.CategorizerItem[];
    sportsOrder: CategorizerWidgetOptions['sportsOrder'];
}): API.CategorizerItem[] {
    if (!sportType) return [];

    const sportsByActiveTab = getSportsBySportType(sports, sportType);

    const sportsToSort = categorizerItems.filter(item =>
        sportsByActiveTab.get(item.value)
    );

    const isSortingApi = features.isActive(features.list.CategorizerSortingApi);

    if (isSortingApi) return sportsToSort;

    const matchOrder = {
        ...CATEGORIZER_STATIC_SPORTS_BY_TYPE,
        ...sportsOrder,
    }[sportType];

    const [staticSports, rest] = partition(
        sportsToSort,
        getCategorizerItemsForStaticSports(matchOrder)
    );
    const sortedStatic = staticSports.sort(
        (a, b) => matchOrder.indexOf(a.value) - matchOrder.indexOf(b.value)
    );

    const sortedRest = rest.sort(
        (a: API.CategorizerItem, b: API.CategorizerItem) =>
            a.name.localeCompare(b.name)
    );

    return sortedStatic.concat(sortedRest);
}

export function getCategorizerItemsForStaticSports(
    staticSports: string[] | readonly string[]
): (categorizerItem: API.CategorizerItem) => boolean {
    return (categorizerItem: API.CategorizerItem) =>
        staticSports.includes(categorizerItem.value);
}

export const getI18nCounties = (): string => {
    const { locale } = getBettingOptions();

    return localeManager.convert(locale, { from: 'cms', to: 'i18nCountries' });
};

export const getNameByCountryCode = (
    formatMessage: IntlFormatters['formatMessage'],
    countryCode?: string
): string => {
    if (!countryCode) return '';

    const region = get(globalMessages, countryCode, undefined);

    if (region && countryCode !== 'US') {
        return formatMessage(region);
    }

    const countryName: string | undefined = getName(
        countryCode,
        getI18nCounties()
    );

    return countryName || countryCode;
};

const getTournamentsInfo = (
    itemsByCountryCode: { [key in string]: API.CategorizerItem[] },
    formatMessage: IntlFormatters['formatMessage']
): TournamentInfo[] =>
    map(itemsByCountryCode, (items, countryCode) => ({
        countryName: getNameByCountryCode(formatMessage, countryCode),
        countryCode,
        items,
    }));

const getRegionOrderRules = (
    sportId: string
): { topRegionsOrder: string[]; topCountriesOrder: string[] } => {
    if (!checkKeyInObj(ORDER_CATEGORIZER_ITEMS_MAPPING, sportId)) {
        return { topRegionsOrder: [], topCountriesOrder: [] };
    }

    const productName = getProductName();

    const mappingByProduct = ORDER_CATEGORIZER_ITEMS_MAPPING[sportId];
    const topCountriesOrder =
        productName === Product.unknown ? [] : mappingByProduct[productName];

    return {
        topRegionsOrder: [
            WORLD_REGIONS_CODES['WW-EUR'],
            WORLD_REGIONS_CODES.WW,
        ],
        topCountriesOrder,
    };
};

function sortByCountryCode<T extends { countryCode: string }>(
    list: T[],
    order: string[]
) {
    return sortBy(list, ({ countryCode }) =>
        findIndex(order, rule => rule === countryCode)
    );
}

function sortByCountryName<T extends { countryName: string }>(list: T[]): T[] {
    return [...list].sort((a, b) =>
        a.countryName.localeCompare(b.countryName, getI18nCounties())
    );
}

const getSortedTournamentsInfo = (
    tournaments: TournamentInfo[],
    sportId: string
): TournamentInfo[] => {
    const { topRegionsOrder, topCountriesOrder } = getRegionOrderRules(sportId);

    const topRegions: TournamentInfo[] = [];
    const otherRegions: TournamentInfo[] = [];
    const excludedRegions: TournamentInfo[] = [];
    const topCountries: TournamentInfo[] = [];
    const otherCountries: TournamentInfo[] = [];

    forEach(tournaments, tournament => {
        const { countryCode } = tournament;

        switch (true) {
            case includes(topRegionsOrder, countryCode): {
                topRegions.push(tournament);
                break;
            }

            case includes(WORLD_REGIONS, countryCode): {
                otherRegions.push(tournament);
                break;
            }

            case includes(topCountriesOrder, countryCode): {
                topCountries.push(tournament);
                break;
            }

            default: {
                otherCountries.push(tournament);
            }
        }
    });

    return concat(
        sortByCountryCode(topRegions, topRegionsOrder),
        sortByCountryName(otherRegions),
        sortByCountryCode(excludedRegions, EXCLUDED_WORLD_REGIONS),
        sortByCountryCode(topCountries, topCountriesOrder),
        sortByCountryName(otherCountries)
    );
};

export const getItemsByLocation = ({
    tournaments,
    sportId,
    formatMessage,
}: {
    tournaments: API.CategorizerItem[];
    sportId: string;
    formatMessage: IntlFormatters['formatMessage'];
}): LocationInfo => {
    const groupedTournaments = groupBy(tournaments, t =>
        getTournamentCountryCode(t)
    );

    const tournamentsInfo = getTournamentsInfo(
        omit(groupedTournaments, 'null'),
        formatMessage
    );

    return {
        tournamentsWithLocation: getSortedTournamentsInfo(
            tournamentsInfo,
            sportId
        ),
        tournamentsWithoutLocation: groupedTournaments.null || [],
    };
};

export const groupCategorizerItemsByOrganizations = (
    categorizerItems: API.CategorizerItem[],
    sportId: string
): CategorizerItemByOrganizations[] => {
    const organizations = ORGANIZATIONS_BY_SPORT[sportId] || [];
    const logoMaps: { [name: string]: string | null } = {};

    const groupedItems = groupBy(categorizerItems, ({ meta }) => {
        const organizationLocalized = meta.find(
            ({ name }) =>
                name === CategorizerTournamentMetaKey.OrganizationLocalized
        );

        const organizationName =
            organizationLocalized?.value || UNGROUPED_CATEGORIES_KEY;

        if (!logoMaps[organizationName]) {
            const organizationLogo = meta.find(
                ({ name }) =>
                    name === CategorizerTournamentMetaKey.OrganizationLogo
            );

            logoMaps[organizationName] = organizationLogo?.value || null;
        }

        return organizationName;
    });

    const categorizerItemByOrganizations = map(groupedItems, (items, name) => {
        return {
            name,
            items,
            logo: logoMaps[name] || null,
            count: sumBy(items, ({ count }) => count),
        };
    });

    const lastIdx = organizations.length;

    return sortBy(categorizerItemByOrganizations, ({ name }) => {
        const idx = organizations.indexOf(name);

        if (name === UNGROUPED_CATEGORIES_KEY) return lastIdx + 1;

        return idx === -1 ? lastIdx : idx;
    });
};

export const checkIsShowLiveLabel = (): boolean => {
    const { matchStatuses } = getCategorizerVars();

    if (!matchStatuses) {
        return !(
            isOneOfPages([App.Routes.OUTRIGHTS_LIST, App.Routes.OUTRIGHT]) ||
            isOneOfPages([App.Routes.ESPORT, App.Routes.CLASSIC], {
                exact: true,
            })
        );
    }

    return intersection(matchStatuses, LIVE_SPORTEVENT_STATUSES).length > 0;
};

export const getSummaryPath = (
    sportId: string
): { search: string; pathname: App.Routes } => {
    return {
        search: stringify({
            sportIds: [sportId],
        }),
        pathname: App.Routes.OUTRIGHTS_LIST,
    };
};

export const getNextOpenedSportIds = (options: {
    actionType: Filters.CheckGameType;
    openedSportIds: string[];
    selectedSportIds: string[];
    triggeredSportId: string;
}): string[] => {
    const { actionType, openedSportIds, triggeredSportId } = options;

    if (actionType === Filters.CheckGameType.Close) {
        return without(openedSportIds, triggeredSportId);
    }

    if (actionType === Filters.CheckGameType.Open) {
        const { selectedSportIds } = options;

        return intersection(openedSportIds, selectedSportIds).concat(
            triggeredSportId
        );
    }

    return [];
};

export const isFreeBetVisibleInCategorizer = (pathname: string): boolean => {
    const sportTypeRoutes = [App.Routes.CLASSIC, App.Routes.ESPORT];
    const timeTypeRoutes = [App.Routes.LIVE_LIST, App.Routes.PREMATCH];

    const combinedRoutes = flatMap(sportTypeRoutes, sportTypeRoute =>
        timeTypeRoutes.map(timeTypeRout => `${sportTypeRoute}${timeTypeRout}`)
    );

    return concat(
        [App.Routes.UPCOMING_LIST],
        sportTypeRoutes,
        timeTypeRoutes,
        combinedRoutes
    ).includes(pathname);
};

export const getNextPathname = (requestedSportType: App.SportType): string => {
    const history = getHistory();

    const slicedPathname = history.location.pathname
        .replace(App.Routes.ESPORT, '')
        .replace(App.Routes.CLASSIC, '');

    const parentPath = getRouteBySportType(requestedSportType);

    return `${parentPath}${slicedPathname}` || App.Routes.UPCOMING_LIST;
};

export const getRequestedSportType = (
    sportIds: string[],
    sports: Map<string, View.Sport>
): App.SportType | null => {
    const sportType = getSportTypeFromLocation();

    if (
        sportType === SportType.Mix ||
        isOneOfPages([App.Routes.OUTRIGHTS_LIST, App.Routes.OUTRIGHT]) ||
        isFavoritePage() ||
        isInnerPage()
    ) {
        return null;
    }

    const nextSportType = getSportTypeBySelectedSportsIds(sports, sportIds);

    return nextSportType && sportType !== nextSportType ? nextSportType : null;
};

const getSportTypeBySelectedSportsIds = (
    sports: Map<string, View.Sport>,
    selectedSportIds: string[]
): App.SportType | null => {
    if (!selectedSportIds.length) return null;

    return getSportTypeBySportTags(sports, selectedSportIds);
};

export const getSelectedCountryKeyInfo = (
    activeCountryKey: string
): {
    selectedCountryKey: string;
    isSelectedCountryCodeWithAll: boolean;
} => {
    const [selectedCountryKey, allPrefix] = activeCountryKey.split(
        ACTIVE_COUNTRY_KEY_DIVIDER
    );

    return {
        selectedCountryKey: selectedCountryKey || '',
        isSelectedCountryCodeWithAll: Boolean(allPrefix),
    };
};

export function sortByTopTournaments(
    categorizerItems: API.CategorizerItem[]
): API.CategorizerItem[] {
    return sortBy(categorizerItems, item =>
        item.meta.find(
            el =>
                el.name === CategorizerTournamentMetaKey.IsTop &&
                el.value === 'true'
        )
    );
}

export function getCategorizerVars(): GQL.GetCategorizerQueryVariables {
    const { selectedSportIds, sportIds: incomeSportIds } = getBettingOptions();
    const sportIds = UserDevice.getInstance()?.isDeviceMobile()
        ? selectedSportIds
        : incomeSportIds;
    const expand = {
        countLiveEventsBySport: true,
        countPreMatchEventsBySport: true,
    };

    if (checkIsDynamicCategorizer()) {
        const {
            marketStatuses,
            matchStatuses,
            ...restVariables
        } = getDefaultVariables();

        const {
            marketStatusesForLiveSportEvent,
            dateFrom: defaultDateFrom,
        } = getVariablesForLiveSportEvent(matchStatuses, marketStatuses);

        return {
            dateFrom: defaultDateFrom,
            matchStatuses,
            ...restVariables,
            marketStatuses: marketStatusesForLiveSportEvent,
            sportIds,
            expand,
        };
    }

    return {
        marketStatuses: [
            GQL.MarketStatus.ACTIVE,
            GQL.MarketStatus.SUSPENDED,
            GQL.MarketStatus.RESULTED,
            GQL.MarketStatus.CANCELLED,
            GQL.MarketStatus.DEACTIVATED,
        ],
        matchStatuses: [
            GQL.SportEventStatus.NOT_STARTED,
            GQL.SportEventStatus.SUSPENDED,
            GQL.SportEventStatus.LIVE,
        ],
        sportEventTypes: [GQL.SportEventType.MATCH],
        sportIds,
        expand,
    };
}

export function hasAnyMetaEventsInCategorizer(
    meta: API.Pair[],
    metaName: CategorizerTournamentMetaKey
): boolean {
    return meta.some(el => el.name === metaName && +el.value > 0);
}

export const getSortedCategorizerItems = ({
    sportType,
    sports,
    sportsOrder,
    categorizerItems,
    outrightCategorizerItems,
}: {
    sportType: App.SportType | null;
    sports: Map<string, View.Sport>;
    sportsOrder: Partial<Record<App.SportType, readonly string[]>> | undefined;
    categorizerItems: API.CategorizerItem[];
    outrightCategorizerItems: API.CategorizerItem[];
}): API.CategorizerItem[] => {
    const outrightItems = outrightCategorizerItems.filter(({ value }) =>
        includes(OUTRIGHTS_ONLY_SPORTS, value)
    );

    const differenceOutrightCategorizer = differenceBy(
        outrightItems,
        categorizerItems,
        'value'
    );

    const categorizerItemResult = [
        ...categorizerItems,
        ...differenceOutrightCategorizer,
    ];

    return getSortedSportsBySportType({
        sportType,
        sports,
        categorizerItems: categorizerItemResult,
        sportsOrder,
    });
};
